import { Add, VisibilityOff } from '@mui/icons-material';
import { Box, Dialog, DialogTitle, IconButton, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Dish, DishVariation, OrderItem } from '../../../apiTypes';
import DataTable, { Column } from '../../../components/DataTable';
import Image from '../../../components/Image';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';

type Row = DbDocument<Dish> & { quantity: number };

type Props = {
  categoryId: string;
  onAdd: (id: string, variadion?: DishVariation) => void;
  items: OrderItem[];
  isList: boolean;
};

const Dishes = ({ categoryId, onAdd, items, isList }: Props) => {
  const { lang } = useLocale();
  const { cafe, getCurrency } = useAdmin();
  const { categories, dishes } = useCafe();
  const [tryToAdd, setTryToAdd] = useState<Row | null>(null);

  const category = useMemo(() => categories.find((c) => c.id === categoryId), [categories, categoryId]);
  const filteredDishes = useMemo(() => dishes.filter((d) => d.categoryId === categoryId), [dishes, categoryId]);
  const data = useMemo(
    () => filteredDishes.map((d) => ({ ...d, quantity: items.filter((i) => i.dishId === d.id).length })),
    [filteredDishes, items]
  );

  const handleClose = useCallback(() => setTryToAdd(null), [setTryToAdd]);

  const handleTryToAdd = useCallback(
    (r: Row) => {
      if (r.variations && r.variations.length > 0) {
        setTryToAdd(r);
      } else {
        onAdd(r.id);
      }
    },
    [onAdd]
  );

  const handleAddVariation = useCallback(
    (id: string, variadion: DishVariation) => {
      onAdd(id, variadion);
      setTryToAdd(null);
    },
    [onAdd]
  );

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'imageURL',
        title: '',
        render: (r: Row) => <Image src={r.imageUrl} width={120} />,
        sx: { width: 120, pr: 0 },
      },
      {
        name: 'name',
        title: t.name[lang],
        align: 'left',
        render: (r: Row) => (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant={isList ? 'body1' : 'caption'}
                sx={{ color: (t) => (r.show ? t.palette.text.primary : t.palette.text.disabled), lineHeight: 1.2 }}
              >
                {r.name}
              </Typography>
              {r.show ? '' : <VisibilityOff fontSize="small" color="disabled" sx={{ ml: 1 }} />}
            </Box>
            {isList && (
              <>
                <Typography variant="body2" color={(t) => t.palette.text.secondary}>
                  {getCurrency(r.price)}
                </Typography>
                <Typography variant="body2" color={(t) => t.palette.text.secondary}>
                  {r.time}&nbsp;{t.minutes_short[lang]}
                </Typography>
              </>
            )}
          </>
        ),
      },
      {
        name: 'quantity',
        title: t.quantity[lang],
        render: (r: Row) => <Typography variant={isList ? 'body1' : 'caption'}>{r.quantity}</Typography>,
      },
      {
        name: 'add',
        title: t.edit[lang],
        render: isList
          ? (r: Row) => (
              <IconButton>
                <Add />
              </IconButton>
            )
          : undefined,
      },
    ],
    [lang, isList, getCurrency]
  );

  if (!category) {
    return null;
  }

  return (
    <>
      <DataTable
        columns={columns}
        rows={data}
        onRowClick={(r) => handleTryToAdd(r)}
        isCard={false}
        showHeader={false}
        isGrid={!isList}
        dataTestIdPrefix="dish"
      />

      <Dialog onClose={handleClose} open={Boolean(tryToAdd)}>
        <DialogTitle>{t.сhoose_variation[lang]}</DialogTitle>
        <List sx={{ pt: 0 }}>
          {tryToAdd?.variations?.map((variation) => (
            <ListItemButton onClick={() => handleAddVariation(tryToAdd.id, variation)} key={variation.name}>
              <ListItemText primary={variation.name} />
            </ListItemButton>
          ))}
        </List>
      </Dialog>
    </>
  );
};

export default Dishes;
